import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import DateSelect from "@/components/DateSelect";
import useModal from "@/hooks/useModal";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { OPEN_ORDER_MUTATION, COMMERCE_AUDIT_LOG_QUERY } from "../queries";
import { OrderType } from "../types";
import { showSnackbar } from "@/lib/snackbarUtils";
import { DateTime } from "luxon";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

export const OpenOrderModal = ({
  onDismiss,
  order,
  refetch
}: {
  onDismiss: () => void;
  order: OrderType;
  refetch: () => void;
}) => {
  const { trackEvent } = useAmplitude();
  const { control, watch } = useForm();
  const [openOrderMutation, { loading }] = useMutation(OPEN_ORDER_MUTATION, {
    refetchQueries: [COMMERCE_AUDIT_LOG_QUERY]
  });
  const headline = "Open Order";
  const [errorContent, setErrorContent] = useState<string>("");
  const defaultDueDate = new Date();
  const [sendNotification, setSendNotification] = useState(false);
  defaultDueDate.setDate(defaultDueDate.getDate() + 60);
  const { close, Modal } = useModal();

  const orderDueDateRange = watch("orderDueDate", {
    startDate: defaultDueDate,
    endDate: defaultDueDate
  });

  const variables = {
    commerceOrderId: order.id,
    dueDate: orderDueDateRange.startDate,
    sendNotification
  };

  async function openCommerceOrder() {
    const { data } = await openOrderMutation({
      variables: { input: variables }
    });

    if (data.openCommerceOrder.success) {
      refetch();
      showSnackbar("Order Opened");
      if (sendNotification) {
        showSnackbar("Email Notification Sent");
      }
      close();
      trackEvent(
        BILLING_ANALYTICS_EVENTS.orderOpened,
        getBillingAnalyticsData({
          source: "orders",
          orderId: order.id,
          action: sendNotification ? "open_with_notification" : "open"
        })
      );
    } else {
      setErrorContent(data.openCommerceOrder.message);
    }
  }

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button onClick={openCommerceOrder} disabled={loading || !orderDueDateRange.startDate}>
          Open Order
        </Button>
      }
      headline={headline}
      className="w-[423px]"
      displayOverflow
    >
      {errorContent ? <div className="text-red-500 font-bold text-center mb-4">{errorContent}</div> : null}
      <p>
        Are you sure you want to open this order for payment collection? The person’s membership band will be extended
        to the end of the applicable dues cycle. This cannot be undone.
      </p>

      <div className="mt-6 flex flex-col gap-6">
        <input className="mt-4 ml-1 absolute"></input>

        <Controller
          name="orderDueDate"
          control={control}
          render={({ field }) => (
            <DateSelect
              label="Order Due Date *"
              useModal={true}
              value={orderDueDateRange}
              onChange={(e) => {
                field.onChange(e);
              }}
              useSingleDate
              showFooter={false}
              shortcuts={{
                next30Days: {
                  text: "+30 days",
                  period: {
                    start: DateTime.now().plus({ days: 30 }).toJSDate(),
                    end: DateTime.now().plus({ days: 30 }).toJSDate()
                  }
                },
                next60Days: {
                  text: "+60 days",
                  period: {
                    start: DateTime.now().plus({ days: 60 }).toJSDate(),
                    end: DateTime.now().plus({ days: 60 }).toJSDate()
                  }
                }
              }}
            />
          )}
        />

        <div>
          <Checkbox
            name="send-notification"
            label="Send email notification to payor"
            checked={sendNotification}
            onChange={() => {
              setSendNotification(!sendNotification);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
